// eslint-disable-next-line no-unused-vars
import { AnimatePresence, motion } from "framer-motion"
import { useEffect, useMemo, useState } from "react"
import { overviewStore } from "../../overview-store"
import { RESOURCE_META } from "@/constants/resource-const"
import { langStore } from "@/stores/lang-store"
import { topUsageRankStore } from "./top-rank-store"
import { formatMessage } from "@/utils/format-message"
import { kpiMetricStore } from "../kpi-metric/kpi-metric-store"
import { topRankBundle } from "./top-rank-const"
import Wrapper from "@/components/wrapper"

const TopRankView = () => {
  const { lang } = langStore()
  const [currentIdx, setCurrentIdx] = useState(0)

  const { selectedTab, resetKeyMap, setResetKey } = overviewStore()
  const { topRankMap } = topUsageRankStore()
  const { kpiMap } = kpiMetricStore() // KPI 데이터를 통해 사용 여부 확인

  // 현재 탭에서 value가 0이 아닌 활성 리소스 키들만 필터링
  const currentKpiItems = kpiMap?.[selectedTab.id]
  const activeKeys = useMemo(() => {
    return currentKpiItems.filter((item) => item.value !== 0).map((item) => item.key)
  }, [currentKpiItems])

  // activeKeys 배열 길이에 맞춰 currentIdx가 범위 초과하지 않도록 보정
  const safeIdx = currentIdx >= activeKeys.length ? 0 : currentIdx
  const currentKey = activeKeys[safeIdx] || activeKeys[0]
  const currentMeta = currentKey ? RESOURCE_META[currentKey] : null

  useEffect(() => {
    if (activeKeys.length <= 1) return

    const timer = setInterval(() => {
      setCurrentIdx((prev) => (prev + 1) % activeKeys.length)
    }, 7000)

    return () => clearInterval(timer)
  }, [resetKeyMap.rank, activeKeys.length])

  const currentRanking = useMemo(() => {
    if (!currentKey) return []
    return topRankMap?.[selectedTab.id]?.[currentKey] || []
  }, [topRankMap, currentKey, selectedTab])

  const maxValue = currentRanking?.[0]?.value || 1

  if (!currentKey || !currentMeta) return <Wrapper.shadow className="p-5 flex flex-col gap-5" />

  return (
    <Wrapper.shadow className="p-5 flex flex-col gap-5 md:min-w-100 h-full">
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <img
            src={currentMeta.imgSrc}
            alt={currentMeta.label[lang]}
            className="w-6 h-6 object-contain"
          />

          <h2 className="text-lg font-bold text-gray-800">
            TOP {currentMeta.label[lang]} {topRankBundle.usage[lang]}
          </h2>
        </div>
      </header>

      {/* 랭킹 */}
      <AnimatePresence mode="wait">
        <motion.div
          key={`${selectedTab.id}-${currentKey}`}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.35 }}
          className="flex flex-col justify-between py-4 gap-4 md:gap-8"
        >
          {currentRanking.map((item, idx) => {
            const percent = (item.value / maxValue) * 100

            return (
              <motion.div
                key={`${currentKey}-${item.dong}`}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{
                  delay: idx * 0.05
                }}
                className="flex items-center md:gap-4"
              >
                <span className="w-5 text-xs text-gray-400 font-medium text-center">{idx + 1}</span>

                <span
                  className={`${lang === "ko" ? "w-16" : "w-18"} font-semibold text-sm text-gray-700`}
                >
                  {formatMessage(topRankBundle.dong[lang], { dong: +item?.dong || 0 })}
                </span>

                <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${percent}%` }}
                    transition={{
                      duration: 0.6,
                      ease: "easeOut"
                    }}
                    className={`
                      h-full rounded-full
                      ${currentMeta.colors.rankBg}
                    `}
                  />
                </div>

                <span className="text-xs font-bold text-gray-800 w-24 text-right">
                  {Number(item.value).toLocaleString()}
                  <span className="ml-1 text-gray-400 font-medium">{currentMeta.unit}</span>
                </span>
              </motion.div>
            )
          })}
        </motion.div>
      </AnimatePresence>

      <div className="mt-auto flex items-center justify-center gap-2 pt-1">
        {activeKeys.map((key, idx) => {
          const isActive = currentKey === key
          const meta = RESOURCE_META[key]

          return (
            <button
              key={key}
              onClick={() => {
                setCurrentIdx(idx)
                setResetKey("rank")
              }}
              className={`
                rounded-full transition-all duration-300
                cursor-pointer
                ${
                  isActive
                    ? `w-6 h-2.5 ${meta.colors.rankBg}`
                    : `w-2.5 h-2.5 bg-gray-300 ${meta.colors.rankBulletHover} hover:scale-110`
                }
              `}
            />
          )
        })}
      </div>
    </Wrapper.shadow>
  )
}

export default TopRankView
